/**
 * Marketing source utilities for shared-components-web.
 *
 * This module provides a local getMarketingSourceCookie() reader and keeps the
 * parseAndSaveMarketingData / sendMarketingDataForLead API functions that are
 * specific to the ClearChoice API integration.
 *
 * Cookie *setting* is intentionally NOT done here — all consumer apps set the
 * marketing_source cookie directly via @aspendental/web-shared-utils so there
 * is a single canonical implementation with no cross-shared-library dependency.
 */

import { NextRouter } from 'next/router';

import postMarketingData from '../services/postMarketingData';

/**
 * Contentful Feature List value that turns on sending gbraid / wbraid to the
 * backend (DCP-14928). Off until the business activates it.
 */
export const SEND_GBRAID_FEATURE = 'sendGbraidIfAvailable';

/** Google iOS click ids; only sent once `sendGbraidIfAvailable` is on. */
export const GOOGLE_BRAID_PARAMS = ['gbraid', 'wbraid'] as const;

/**
 * Drops gbraid / wbraid from a marketing query string (e.g. the
 * marketing_source cookie) so only gclid-style attribution is sent while
 * `sendGbraidIfAvailable` is off. Returns the input unchanged when it has
 * neither.
 */
export function withoutBraidParams(query: string): string {
	const params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
	if (!GOOGLE_BRAID_PARAMS.some((key) => params.has(key))) return query;
	for (const key of GOOGLE_BRAID_PARAMS) params.delete(key);
	return params.toString();
}

/**
 * Read and URL-decode the marketing_source cookie value.
 * Returns an empty string if the cookie is absent or the environment has no
 * document (e.g. SSR).
 */
export function getMarketingSourceCookie(): string {
	if (typeof document === 'undefined') return '';
	const match = document.cookie
		.split(';')
		.map((c) => c.trim())
		.find((c) => c.startsWith('marketing_source='));
	if (!match) return '';
	try {
		return decodeURIComponent(match.slice('marketing_source='.length));
	} catch {
		return match.slice('marketing_source='.length);
	}
}

// Parse and save marketing data function - now always called for every lead
export const parseAndSaveMarketingData = async (
	marketingSourceCookie: string,
	leadId: string,
	baseUrl: string,
	headers: Record<string, string>,
	router: NextRouter,
	sessionId: string,
	formId: string,
	referrer?: string,
	transformMarketingSourceEnabled?: boolean
) => {
	const cleanCookie = marketingSourceCookie.startsWith('?') ? marketingSourceCookie.slice(1) : marketingSourceCookie;
	const urlParams = new URLSearchParams(cleanCookie);

	// Check if trans_id exists - only proceed if it does
	const transIdValue = urlParams.get('trans_id');
	const hasTransId = transIdValue && transIdValue.trim() !== '';

	if (!hasTransId) {
		// Return early if no valid trans_id exists
		return;
	}

	// Initialize marketing data with required fields
	const marketingData: Record<string, unknown> = {
		session_id: sessionId,
		timestamp: Date.now(),
		domain: 'https://clearchoice.com',
		pathname: router.pathname || '/',
		form_id: formId,
		trans_id: [transIdValue],
	};

	for (const [key, value] of urlParams.entries()) {
		if (key !== 'trans_id') {
			marketingData[key] = value;
		}
	}

	if (transformMarketingSourceEnabled) {
		marketingData.referrer = referrer || document.referrer || '';
	}

	const apiURL = `${baseUrl}/leads/${leadId}/marketing-data`;
	const payload = {
		leadId,
		marketingData,
	};

	return await postMarketingData(apiURL, payload, headers);
};

export const sendMarketingDataForLead = async (
	leadId: string,
	baseUrl: string,
	headers: Record<string, string>,
	router: NextRouter,
	sessionId: string,
	formId: string,
	referrer?: string,
	transformMarketingSourceEnabled?: boolean
) => {
	const marketingSourceCookie = getMarketingSourceCookie();

	return await parseAndSaveMarketingData(
		marketingSourceCookie,
		leadId,
		baseUrl,
		headers,
		router,
		sessionId,
		formId,
		referrer,
		transformMarketingSourceEnabled
	);
};
